import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Loader2,
  BarChart3,
  Eye,
  CalendarIcon,
  Zap,
  TrendingDown,
  Users,
} from "lucide-react";

type DatePreset = "today" | "yesterday" | "week" | "30days" | "custom";

interface EmployeeCost {
  profile_id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  total_tokens: number;
  total_spend: number;
  transaction_count: number;
}

interface CostAnalysisProps {
  onViewDetail: (profileId: string, profileName: string) => void;
}

function getDateRange(preset: DatePreset, customFrom?: Date, customTo?: Date): { from: string; to: string } {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  switch (preset) {
    case "today":
      return { from: todayStart.toISOString(), to: tomorrowStart.toISOString() };
    case "yesterday": {
      const yesterdayStart = new Date(todayStart);
      yesterdayStart.setDate(yesterdayStart.getDate() - 1);
      return { from: yesterdayStart.toISOString(), to: todayStart.toISOString() };
    }
    case "week": {
      const weekAgo = new Date(todayStart);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { from: weekAgo.toISOString(), to: tomorrowStart.toISOString() };
    }
    case "30days": {
      const monthAgo = new Date(todayStart);
      monthAgo.setDate(monthAgo.getDate() - 30);
      return { from: monthAgo.toISOString(), to: tomorrowStart.toISOString() };
    }
    case "custom": {
      const from = customFrom ? new Date(customFrom.getFullYear(), customFrom.getMonth(), customFrom.getDate()) : todayStart;
      const to = customTo ? new Date(customTo.getFullYear(), customTo.getMonth(), customTo.getDate() + 1) : tomorrowStart;
      return { from: from.toISOString(), to: to.toISOString() };
    }
  }
}

const presetLabels: Record<DatePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  week: "Last 7 Days",
  "30days": "Last 30 Days",
  custom: "Custom Range",
};

const CostAnalysis = ({ onViewDetail }: CostAnalysisProps) => {
  const { profile } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [employeeCosts, setEmployeeCosts] = useState<EmployeeCost[]>([]);
  const [activePreset, setActivePreset] = useState<DatePreset>("30days");
  const [customFrom, setCustomFrom] = useState<Date | undefined>();
  const [customTo, setCustomTo] = useState<Date | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchCostData = useCallback(async () => {
    if (!profile?.client_id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { from, to } = getDateRange(activePreset, customFrom, customTo);

    // Fetch debit transactions (consumption) within the date range
    const { data: txData, error: txError } = await supabase
      .from("wallet_transactions")
      .select("profile_id, tokens_used, amount")
      .eq("client_id", profile.client_id)
      .eq("type", "debit")
      .gte("created_at", from)
      .lt("created_at", to);

    if (txError) {
      toast({ title: "Error loading cost data", description: txError.message, variant: "destructive" });
      setLoading(false);
      return;
    }

    // Group by profile_id
    const grouped: Record<string, { total_tokens: number; total_spend: number; transaction_count: number }> = {};
    for (const tx of txData ?? []) {
      const pid = tx.profile_id ?? "__unknown__";
      if (!grouped[pid]) {
        grouped[pid] = { total_tokens: 0, total_spend: 0, transaction_count: 0 };
      }
      grouped[pid].total_tokens += tx.tokens_used ?? 0;
      grouped[pid].total_spend += Math.abs(tx.amount);
      grouped[pid].transaction_count += 1;
    }

    // Fetch profile info for all profile_ids
    const profileIds = Object.keys(grouped).filter((id) => id !== "__unknown__");
    let profileMap: Record<string, { full_name: string | null; avatar_url: string | null; email: string | null }> = {};

    if (profileIds.length > 0) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, email")
        .in("id", profileIds);

      if (profileData) {
        profileMap = Object.fromEntries(
          profileData.map((p) => [p.id, { full_name: p.full_name, avatar_url: p.avatar_url, email: p.email }])
        );
      }
    }

    const result: EmployeeCost[] = Object.entries(grouped)
      .map(([pid, stats]) => ({
        profile_id: pid,
        full_name: pid === "__unknown__" ? "Unknown" : (profileMap[pid]?.full_name ?? "Unnamed"),
        avatar_url: profileMap[pid]?.avatar_url ?? null,
        email: profileMap[pid]?.email ?? null,
        ...stats,
      }))
      .sort((a, b) => b.total_spend - a.total_spend);

    setEmployeeCosts(result);
    setLoading(false);
  }, [profile?.client_id, activePreset, customFrom, customTo, toast]);

  useEffect(() => {
    fetchCostData();
  }, [fetchCostData]);

  const totals = useMemo(() => {
    const totalTokens = employeeCosts.reduce((sum, e) => sum + e.total_tokens, 0);
    const totalSpend = employeeCosts.reduce((sum, e) => sum + e.total_spend, 0);
    return { totalTokens, totalSpend };
  }, [employeeCosts]);

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handlePresetChange = (preset: DatePreset) => {
    setActivePreset(preset);
    if (preset !== "custom") {
      setCalendarOpen(false);
    }
  };

  const handleCustomApply = () => {
    if (customFrom && customTo) {
      setActivePreset("custom");
      setCalendarOpen(false);
    }
  };

  if (!profile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <BarChart3 className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client associated with your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      <div>
        <h1 className="text-2xl font-semibold">Cost Analysis</h1>
        <p className="text-sm text-muted-foreground">
          Analyse token consumption and cost per employee.
        </p>
      </div>

      {/* Date filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {(["today", "yesterday", "week", "30days"] as DatePreset[]).map((preset) => (
          <Button
            key={preset}
            variant={activePreset === preset ? "default" : "outline"}
            size="sm"
            onClick={() => handlePresetChange(preset)}
          >
            {presetLabels[preset]}
          </Button>
        ))}

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={activePreset === "custom" ? "default" : "outline"}
              size="sm"
              className="gap-1.5"
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              {activePreset === "custom" && customFrom && customTo
                ? `${customFrom.toLocaleDateString()} – ${customTo.toLocaleDateString()}`
                : "Custom Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium">Select date range</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">From</p>
                  <Calendar
                    mode="single"
                    selected={customFrom}
                    onSelect={setCustomFrom}
                    disabled={(date) => date > new Date()}
                    initialFocus
                  />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">To</p>
                  <Calendar
                    mode="single"
                    selected={customTo}
                    onSelect={setCustomTo}
                    disabled={(date) => date > new Date() || (customFrom ? date < customFrom : false)}
                  />
                </div>
              </div>
              <Button size="sm" onClick={handleCustomApply} disabled={!customFrom || !customTo}>
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {loading ? "—" : employeeCosts.length}
            </div>
            <p className="text-xs text-muted-foreground">with activity in period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens Used</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {loading
                ? "—"
                : totals.totalTokens >= 1_000_000
                ? `${(totals.totalTokens / 1_000_000).toFixed(2)}M`
                : totals.totalTokens >= 1_000
                ? `${(totals.totalTokens / 1_000).toFixed(1)}K`
                : totals.totalTokens.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">in selected period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
            <TrendingDown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-destructive">
              {loading ? "—" : totals.totalSpend.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">points consumed</p>
          </CardContent>
        </Card>
      </div>

      {/* Employee table */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Employee Breakdown
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : employeeCosts.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No consumption data found for this period.</p>
          </Card>
        ) : (
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Transactions</TableHead>
                  <TableHead className="text-right">Tokens Consumed</TableHead>
                  <TableHead className="text-right">Total Spend (pts)</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employeeCosts.map((emp) => (
                  <TableRow key={emp.profile_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={emp.avatar_url ?? undefined} />
                          <AvatarFallback className="text-xs">
                            {getInitials(emp.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">
                            {emp.full_name || <span className="text-muted-foreground italic">Unnamed</span>}
                          </span>
                          {emp.email && (
                            <span className="text-xs text-muted-foreground">{emp.email}</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {emp.transaction_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {emp.total_tokens >= 1_000_000
                        ? `${(emp.total_tokens / 1_000_000).toFixed(2)}M`
                        : emp.total_tokens >= 1_000
                        ? `${(emp.total_tokens / 1_000).toFixed(1)}K`
                        : emp.total_tokens.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-destructive">
                      {emp.total_spend.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => onViewDetail(emp.profile_id, emp.full_name ?? "Employee")}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View Detail
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CostAnalysis;
